<template>
  <Form @submit="onSubmit">
    <div class="flex border-b border-dark-b">
      <h2 class="text-xl md:text-2xl font-semibold mr-6 w-9/12 md:w-1/2">Profile Details</h2>
      <div class="flex lg:pl-2 text-xs items-center w-1/2 justify-end md:justify-between">
        <div>Progress</div>
        <div class="flex md:w-10/12 items-center justify-between">
          <div class="hidden md:inline w-1/5 h-2 ml-2 bg-green rounded-full"/>
          <div class="hidden md:inline w-4/5 h-2 bg-white rounded-r-full"/>
          <div class="pl-2 text-green md:text-black">20%</div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full pt-12">
      <div class="lg:pr-5 w-full font-semibold text-lg lg:text-xl lg:w-1/2">
        <div class="pb-1">First Name<span class="text-error">*</span></div>
        <Field
          name="firstName"
          class="form-input"
          placeholder="e.g. John"
          :rules="isRequired"
          v-model="formData.first_name"
        />
        <div class="h-5">
          <ErrorMessage name="firstName" class="form-error" />
        </div>
      </div>
      <div class="lg:pl-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">Last Name<span class="text-error">*</span></div>
        <Field
          name="lastName"
          class="form-input"
          placeholder="e.g. Doe"
          :rules="isRequired"
          v-model="formData.last_name"
        />
        <div class="h-5">
          <ErrorMessage name="lastName" class="form-error" />
        </div>
      </div>     
    </div>
    <div class="flex flex-wrap w-full">
      <div class="lg:pr-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">
          Email <span class="lg:text-sm font-normal">(Username)</span>
          <span class="text-error">*</span>
        </div>
        <Field
          name="email"
          class="form-input"
          placeholder="e.g. email@website.com"
          :rules="validateEmail"
          v-model="formData.email"
        />
        <div class="h-5">
          <ErrorMessage name="email" class="form-error" />
        </div>
      </div>
      <div class="lg:pl-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">Public Display Name</div>
        <Field
          name="publicDisplayName"
          class="border border-dark-b w-full h-12 px-3 rounded-lg bg-gray-b"
          placeholder="JohnD"
          color="bg-gray-b"
          disabled
          v-model="publicDisplayName"
        />
        <div class="h-5 lg:h-7"/>
      </div>           
    </div>
    <div class="flex flex-wrap w-full">
      <div class="lg:pr-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">Phone Number<span class="text-error">*</span></div>
        <VueTelInput
          name="phone"
          mode="international"
          ref="telInput"
          :value="phone"
          @input="onPhoneInput"
        />
        <div class="h-5 lg:h-7">
          <span
            v-if="!phone && phoneError"
            class="form-error"
          >This field is required.</span>
        </div> 
      </div>
      <div class="lg:pl-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">WeChat ID</div>
        <Field
          name="wechatId"
          class="form-input"
          v-model="formData.wechat_id"
        />
        <div class="h-5 lg:h-7"/>
      </div>     
    </div>
    <div class="flex flex-wrap w-full items-end">
      <div class="lg:pr-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">LinkedIn Profile Link</div>
        <Field
          name="linkedInUrl"
          class="form-input"
          v-model="formData.linked_in_profile_link"
        />
        <div class="h-5 lg:h-7"/> 
      </div>
      <div class="lg:pl-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">
          CV/Resume<span class="text-error">*</span>
          <span class="text-sm font-normal hidden lg:inline">(Word, PDF, Jpeg, Png format only.Max size 6MB)</span>
        </div>
        <div class="lg:hidden text-base font-normal">
          <span>(Word, PDF, Jpeg, Png format only.Max size 6MB)</span>
        </div>
        <div class="w-full h-12 px-3 relative flex items-center">
          <Field
            name="resume"
            type="file"
            class="absolute inset-0 opacity-0 z-10 w-full overflow-hidden"
            :rules="fileRequired"
            v-model="formData.cv"
          />
          <span
            v-if="formData.cv && formData.cv[0]"
            class="pointer-events-none absolute z-10 left-0 pl-4 w-4/6 truncate text-blue text-lg lg:text-xl font-normal"
          >
            {{ formData.cv[0].name }}
          </span>
          <span
            v-else
            class="pointer-events-none truncate w-9/12 absolute text-sm z-10 left-0 px-4 opacity-50"
          >
            Word, PDF, Jpeg, Png format only
          </span>
          <div v-if="formData.cv && formData.cv[0]" class="w-9/12 z-10 pointer-events-none">
            <img src="@/assets/icons/closeBlack.svg" class="ml-auto z-20 pointer-events-auto cursor-pointer" @click="formData.cv = null"/>
          </div>
          <img src="@/assets/icons/uploadBlack.svg" class="ml-auto w-12 h-12">
        </div>
        <div class="h-7">
          <ErrorMessage name="resume" class="form-error" />
        </div>
      </div>     
    </div>
    <div class="flex flex-wrap w-full">
      <div class="font-semibold text-lg lg:text-xl w-full">
        <div class="pb-1">Address Line 1</div>
        <Field
          name="address1"
          class="form-input"
          v-model="formData.address_line_1"
        />
        <div class="h-7"/> 
      </div>
    </div>
    <div class="flex flex-wrap w-full">
      <div class="font-semibold text-lg lg:text-xl w-full">
        <div class="pb-1">Address Line 2</div>
        <Field
          name="address2"
          class="form-input"
          v-model="formData.address_line_2"
        />
        <div class="h-7"/> 
      </div>
    </div>
    <div class="flex flex-wrap w-full">
      <div class="font-semibold text-lg lg:text-xl w-full">
        <div class="pb-1">Address Line 3</div>
        <Field
          name="address3"
          class="form-input"
          v-model="formData.address_line_3"
        />
        <div class="h-7"/> 
      </div>
    </div>
    <div class="flex flex-wrap w-full">
      <div class="lg:pr-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">City</div>
        <Field
          name="city"
          class="form-input"
          v-model="formData.city"
        />
        <div class="h-7"/> 
      </div>
      <div class="lg:pl-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">State/Province</div>
        <Field
          name="stateProvince"
          class="form-input"
          v-model="formData.state"
        />
        <div class="h-7"/> 
      </div>
    </div>
    <div class="flex flex-wrap w-full">
      <div class="lg:pr-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">Country/Region<span class="text-error">*</span></div>
        <CountrySelect
          name="country"
          placeholder="Choose your Country/Region"
          class="form-input focus:text-opacity-100"
          ref="countrySelect"
          :country="formData.country"
          :class="{'text-dark text-opacity-50': !formData.country}"
          v-model="formData.country"
        />
        <div class="h-7">
          <span
            v-if="!formData.country && countryError"
            class="form-error"
          >This field is required.</span>
        </div>
      </div>
      <div class="lg:pl-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1">Pin Code/Zip Code</div>
        <Field
          name="pinCode"
          class="form-input"
          v-model="formData.pincode"
        />
        <div class="h-7"/> 
      </div>   
    </div>
    <div class="flex border-b border-dark-b pt-9">
      <h2 class="text-xl md:text-2xl font-semibold mr-6">Product Expertise</h2>
    </div>
    <div class="pt-12">
      <div class="hidden lg:block text-xl font-semibold">
        Product Category, Sub-Category & Years of Experience<span class="text-error">*</span>
        <span class="text-sm font-normal"> (Max 5)</span>
      </div>
      <div class="flex flex-wrap lg:flex-nowrap items-center">
        <div class="w-full lg:w-1/2 lg:pr-5 font-semibold text-lg">
          <p class="lg:hidden text-lg pb-1">
            Product Category<span class="text-error">*</span>
            <span class="text-base font-normal"> (Max 5)</span>
          </p>
          <Field
            name="category"
            as="select"
            class="border border-dark-b w-full h-12 pl-3 pr-8 rounded-lg bg-white text-dark text-opacity-50 focus:text-opacity-100 truncate"
            v-model="experience.category"
          >
            <option value disabled>Choose a Product Category</option>
            <option
              v-for="(cat, index) in getCategories"
              :key="`categories-${index}`"
              :value="cat.category_id_token"
            >
              {{ cat.category_name }}
            </option>
          </Field>
          <div class="h-7 lg:hidden">
            <span
              v-if="!experiences.length && experienceError"
              class="form-error"
            >This field is required.</span>
          </div>
        </div>
        <div class="flex flex-wrap w-full lg:w-1/2 lg:flex-nowrap lg:pl-5 items-center justify-between">
          <div class="w-full lg:w-4/6 lg:pr-8 font-semibold text-lg">
            <p class="lg:hidden text-lg pb-1">
              Sub-Category<span class="text-error">*</span>
              <span class="text-base font-normal"> (Max 5)</span>
            </p>
            <Field
              name="subCategory"
              as="select"
              class="border border-dark-b w-full h-12 pl-3 pr-8 rounded-lg bg-white truncate text-dark text-opacity-50 focus:text-opacity-100"
              v-model="experience.subCategory"
            >
              <option value disabled>Choose Sub-Category</option>
              <template v-if="selectedCategory">
                <option
                  v-for="(sub, index) in subCategories"
                  :key="`sub-category-${index}`"
                  :value="sub.sub_category_id"
                >
                  {{ sub.sub_category_name }}
                </option>
              </template>
              <template v-else>
                <option value="1"></option>
              </template>
            </Field>
            <div class="h-7 lg:hidden">
              <span
                v-if="!experiences.length && experienceError"
                class="form-error"
              >This field is required.</span>
            </div>
          </div>
          <div class="w-full lg:w-3/11 lg:pr-3 font-semibold text-lg">
            <p class="lg:hidden text-lg pb-1">
              Years of Experience<span class="text-error">*</span>
              <span class="text-base font-normal"> (Max 5)</span>
            </p>
            <div class="flex justify-between items-center">
              <Field
                name="years"
                as="select"
                class="border border-dark-b flex-grow md:w-full h-12 pl-3 pr-8 rounded-lg truncate bg-white text-dark text-opacity-50 focus:text-opacity-100 mr-2 lg:mr-0"
                v-model="experience.years"
              >
                <option value disabled>Year(s)</option>
                <option
                  :key="`experience-0`"
                  :value="0.5"
                ><span>Less than </span>1 Year</option>
                <option
                  v-for="(val) in 30"
                  :key="`experience-${val}`"
                  :value="val"
                >{{ val }} Year<span v-if="val > 1">s</span></option>
                <option
                  :key="`experience-31`"
                  :value="31"
                ><span>More than </span>30 Years</option>
              </Field>
              <button
                class="lg:hidden w-12 h-12 bg-dark-blue rounded-lg text-white text-xl font-medium"
                type="button"
                :disable="experiences.length > 4"
                :class="{'opacity-50': experiences.length > 4}"
                @click="addExperience"
              >+</button>
            </div>
          </div>
          <button
            class="hidden lg:inline-block w-12 h-12 rounded-lg bg-dark-blue text-white text-xl font-medium"
            type="button"
            :disable="experiences.length > 4"
            :class="{'opacity-50': experiences.length > 4}"
            @click="addExperience"
          >+</button>
        </div>
      </div>
      <div class="h-3 lg:h-5">
        <span
          v-if="!experiences.length && experienceError"
          class="form-error"
        >This field is required.</span>
      </div>
      <div class="flex flex-wrap">
        <div
          v-for="(exp, index) in experiences"
          :key="`selected-${index}`"
          class="text-xs lg:text-base pr-2 pb-2"
        >
          <div class="bg-gray-b px-2 py-1">
            <span class="pr-2">
              {{exp.category.name}} - {{exp.subCategory.name}} - <span v-if="exp.years > 1 && exp.years < 31">{{exp.years}} Years</span> <span v-else-if="exp.years > 30">More than 30 Years</span><span v-else-if="exp.years < 1">Less than 1 Year</span><span v-else>1 Year</span>
            </span>
            <span
              class="cursor-pointer text-xl"
              @click="removeExperience(index)"
            >x</span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end pt-6 pb-1">
      <button
        class="bg-blue text-white rounded-full py-3 px-8 text-lg lg:text-xl font-bold"
        @click="validateFields"
      >
        Save
      </button>
    </div>
  </Form>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import VeeValidate from '@/shared/mixins/VeeValidate'
import { VueTelInput } from 'vue3-tel-input'
import 'vue3-tel-input/dist/vue3-tel-input.css'

export default {
  name: 'Profile',
  mixins: [VeeValidate],
  components: { VueTelInput },
  data() {
    return {
      formData: {},
      experiences: [],
      phone: '',
      experience: {
        category: '',
        subCategory: '',
        years: ''
      },
      countryError: false,
      experienceError: false,
      phoneError: false,
      serverBaseUrl: process.env.VUE_APP_SERVER_BASE_URL,
      topCountries: ['VN','TH','MY','ID','IN','HK','CN']
    }
  },
  mounted() {
    if (!this.categories || !this.categories.length) this.fetchCategories()
    const telInput = this.$refs.telInput.$el
    telInput.getElementsByTagName('input')[0].placeholder = "e.g. 1234 5678"
    this.setTopCountries()    
  },
  watch: {
    selectedCategory() {
      this.experience.subCategory = ''
    }
  },
  computed: {
    ...mapGetters([
      'getCategories'
    ]),
    publicDisplayName() {
      return `${(this.formData.first_name ? this.formData.first_name : '') + (this.formData.last_name ? this.formData.last_name.charAt(0) : '')}`
    },
    categories() {
      return this.getCategories.length ? this.getCategories : []
    },
    subCategories() {
      return this.selectedCategory ? this.selectedCategory.sub_categories : []
    },
    selectedCategory() {
      return this.categories.find(cat => cat.category_id_token === this.experience.category)
    },
    selectedSubCategory() {
      return this.subCategories.find(sub => sub.sub_category_id === this.experience.subCategory)
    },
    isValidExperience() {
      return !this.isDuplicateExperience && this.experience.category && this.experience.subCategory && this.experience.years
    },
    isDuplicateExperience() {
      return this.experiences.find(exp => (exp.category.id === this.experience.category) && (exp.subCategory.id === this.experience.subCategory))
    },
    experienceToAdd() {
      return {
        category: {
          id: this.selectedCategory.category_id_token,
          name: this.selectedCategory.category_name
        },
        subCategory: {
          id: this.selectedSubCategory.sub_category_id,
          name: this.selectedSubCategory.sub_category_name
        },
        years: this.experience.years
      }
    },
    isCustomError() {
      return this.countryError || this.experienceError || this.phoneError
    }
  },
  methods: {
    ...mapActions([
      'fetchCategories',
      'updateProfileBasicInfo'
    ]),
    setTopCountries() {
      const selectTag = this.$refs.countrySelect.$el
      const placeHolderTag = selectTag.firstChild
      const hk = selectTag.querySelector('[value="HK"]')
      hk.innerHTML = 'Hong Kong SAR'
      this.topCountries.forEach(country => {
        const countryTag = selectTag.querySelector(`[value=${country}`)
        placeHolderTag.after(countryTag)
      })
    },
    onPhoneInput(phone, phoneObject) {
      if (phoneObject?.formatted) {
        this.phone = phoneObject.formatted
        this.formData.country_code = phoneObject.country.dialCode
      }
    },
    addExperience() {
      if (this.experiences.length < 5 && this.isValidExperience) {
        this.experiences.push(this.experienceToAdd)
        this.experience = {}
        this.experienceError = false
      }
    },
    removeExperience(index) {
      this.experiences.splice(index, 1)
    },
    validateFields() {
      this.countryError = !this.formData.country
      this.experienceError = !this.experiences.length
      this.phoneError = !this.phone
    },
    async onSubmit() {
      if (this.isCustomError) return
      this.formData.phone_number = this.phone.replace(/[^0-9]/g, '')
      const response = await this.updateProfileBasicInfo(this.formatFormData())
      this.$notify({
        group: 'buyHive',
        type: response.result ? 'success' : 'error',
        text: response.message
      })
      if (response.result) this.$store.commit('setUserName',`${this.formData.first_name} ${this.formData.last_name}`)
    },
    formatFormData() {
      const userData = new FormData()
      Object.keys(this.formData).forEach(key => {
        if (key !== 'cv') userData.append(key, this.formData[key] || '')
      })
      userData.append('cv', this.formData.cv[0])
      userData.append('_method', 'PUT')

      this.experiences.forEach((exp, index) => {
        userData.append(`experience[${index}][sub_category]`, exp.subCategory.id)
        userData.append(`experience[${index}][years]`, exp.years)
      })
      return userData
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep(.vue-tel-input) {
  @apply border-none w-full h-12 rounded-lg;
  .vti__dropdown {
    @apply bg-white w-22 rounded-lg mr-3;
  }
  .vti__input {
    @apply rounded-lg;
  }
  &:focus-within {
    box-shadow: none;
  }
}
</style>